import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  PrintProduct,
  CartUpdateItem,
  isDigitalDownload
} from "../../../CheckoutPage/components/types";
import {
  updateCartItems,
  fetchPrintProductsForPhoto,
  fetchCart
} from "../../../CheckoutPage/components/api";
import { firstTierOfferText, applicableTier, discountedUnitPrice } from "../../../CheckoutPage/components/discountTiers";
const starIcon = require("../../../PrintProducts/styles/star.svg");
const truckIcon = require("../../../PrintProducts/styles/truck.svg");
import "./LightBoxPrintProducts.css";
import PrintLightboxHeader from "./PrintLightboxHeader";
import PrintProductPreview from "./PrintProductPreview";
import PrintProductZoomDrawer from "./PrintProductZoomDrawer";
import MagnifyIcon from "../vectors/MagnifyIcon";
import Telemetry from "../../../../utils/rudderstackAnalytics";

interface LightBoxPrintProductsProps {
  photo: any;
  event: any;
  cartId: number | null;
  primaryColor: string;
  updateCartFromApiResponse?: (apiResponse: any) => void;
  onCartStatusChange?: (hasCartItems: boolean) => void;
  canShowEmailPopup?: () => boolean;
  showSaveCartModal?: (photo: any, cartItem: any) => void;
  cart?: any;
  isEmbed?: boolean;
  isPrepaidActive?: boolean;
  setSelfieUploadPopup?: (value: boolean) => void;
}

const LightBoxPrintProducts: React.FC<LightBoxPrintProductsProps> = ({
  photo,
  event,
  cartId,
  primaryColor,
  updateCartFromApiResponse,
  onCartStatusChange,
  canShowEmailPopup,
  showSaveCartModal,
  cart,
  isEmbed,
  isPrepaidActive,
  setSelfieUploadPopup
}) => {
  const [printProducts, setPrintProducts] = useState<PrintProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [digitalSelected, setDigitalSelected] = useState(false);
  const [productQuantities, setProductQuantities] = useState<{
    [key: number]: number;
  }>({});
  const [existingSales, setExistingSales] = useState<any[]>([]);
  const [expandedDescriptions, setExpandedDescriptions] = useState<{
    [key: number]: boolean;
  }>({});
  const [activeProductId, setActiveProductId] = useState<number | null>(null);
  const [pinnedProductId, setPinnedProductId] = useState<number | null>(null);
  const [activeThumbY, setActiveThumbY] = useState<number>(0);
  const [isWideDesktop, setIsWideDesktop] = useState(window.innerWidth >= 1281);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [shippingUnlockedVisible, setShippingUnlockedVisible] = useState(false);
  const shippingUnlockedProductIdRef = useRef<number | null>(null);
  const [unlockedBannerBottom, setUnlockedBannerBottom] = useState<number>(0);
  const printProductsPanelRef = useRef<HTMLDivElement>(null);
  const [shippingReminderVisible, setShippingReminderVisible] = useState(false);
  const prevShippingReminderRef = useRef<boolean>(cart?.freeShippingReminderActive ?? false);
  const hasAnimatedRef = useRef<boolean>(false);
  const closeTimerRef = useRef<number | null>(null);
  const thumbnailRefs = useRef<Map<number, HTMLElement>>(new Map());
  const prevShippingEarnedRef = useRef<boolean>(cart?.freeShippingEarned || false);
  const [bundleAppliedVisible, setBundleAppliedVisible] = useState(false);
  const prevBundleAvailedRef = useRef<boolean>(
    !!(cart?.photoBundleDiscountAvailed && cart?.photoBundleDiscountEventId === event.id)
  );

  const digitalIsPrepaidFree = existingSales.some(
    (sale: any) => isDigitalDownload(sale.productType) && sale.prepaid
  );

  useEffect(() => {
    setDigitalSelected(false);
    setProductQuantities({});
    setExistingSales([]);
    setExpandedDescriptions({});
    setActiveProductId(null);
    setPinnedProductId(null);
    fetchPrintProductsData();
    initializeFromCart();
  }, [event.id, photo.id, cartId]);

  useEffect(() => {
    if (!cart) return;
    const prev = prevShippingEarnedRef.current;
    const current = cart.freeShippingEarned || false;
    prevShippingEarnedRef.current = current;
    if (!current) {
      setShippingUnlockedVisible(false);
      return;
    }
    if (prev || !event.hasPrintShippingIncentive || !event.printFreeShippingThreshold) return;
    setShippingUnlockedVisible(true);
    const id = window.setTimeout(() => setShippingUnlockedVisible(false), 2500);
    return () => clearTimeout(id);
  }, [cart?.freeShippingEarned]);

  useEffect(() => {
    const current = !!(
      cart?.photoBundleDiscountAvailed &&
      cart?.photoBundleDiscountEventId === event.id
    );
    const prev = prevBundleAvailedRef.current;
    prevBundleAvailedRef.current = current;
    if (!current) {
      setBundleAppliedVisible(false);
      return;
    }
    if (prev) return;
    setBundleAppliedVisible(true);
    const id = window.setTimeout(() => setBundleAppliedVisible(false), 2700);
    return () => clearTimeout(id);
  }, [cart?.photoBundleDiscountAvailed, cart?.photoBundleDiscountEventId, event.id]);

  useEffect(() => {
    const isActive = cart?.freeShippingReminderActive ?? false;
    const prev = prevShippingReminderRef.current;
    prevShippingReminderRef.current = isActive;
    if (!isActive || prev) return;
    setShippingReminderVisible(true);
    const id = window.setTimeout(() => setShippingReminderVisible(false), 4000);
    return () => clearTimeout(id);
  }, [cart?.freeShippingReminderActive]);

  useLayoutEffect(() => {
    if (!shippingUnlockedVisible || !shippingUnlockedProductIdRef.current || !printProductsPanelRef.current) return;
    const card = printProductsPanelRef.current.querySelector<HTMLElement>(
      `[data-product-id="${shippingUnlockedProductIdRef.current}"]`
    );
    if (!card) return;
    const panelRect = printProductsPanelRef.current.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    setUnlockedBannerBottom(Math.max(0, panelRect.bottom - cardRect.bottom));
  }, [shippingUnlockedVisible]);

  const prepaidFree = digitalIsPrepaidFree || !!isPrepaidActive;
  const digitalDiscountColor = prepaidFree ? "#8013AE" : "#00A8FF";

  const digitalDiscountTeaser = event.hasDiscount && !prepaidFree
    ? event.discountType === "price_break"
      ? event.discountMinQuantity
        ? `Buy ${event.discountMinQuantity} to get ${event.discountPercentage}% off`
        : `${event.discountPercentage}% off`
      : event.discountType === "multi_tier" && event.discountFirstTierQuantity
      ? `Buy ${event.discountFirstTierQuantity} for ${event.discountFirstTierFormattedPrice} ${event.currency}`
      : event.discountType === "photo_bundle" && event.discountPhotoBundleFormattedPrice
      ? (
          <>
            <span className="apply">Apply bundle</span> <span className="desc">Get all your photos for {event.discountPhotoBundleFormattedPrice} {event.currency}</span>
          </>
        )
      : null
    : null;

  const digitalSale = existingSales.find((sale: any) =>
    isDigitalDownload(sale.productType)
  );
  const digitalDiscountApplied =
    !!digitalSale?.digitalDiscountedTotalFormatted && !prepaidFree;
  const isBundleDiscountActive = event.discountType === "photo_bundle" && event.hasDiscount;
  const digitalBundleActive =
    !prepaidFree &&
    event.discountType === "photo_bundle" &&
    !!cart?.photoBundleDiscountAvailed &&
    cart?.photoBundleDiscountEventId === event.id;
  const digitalDiscountShown = digitalDiscountApplied || digitalBundleActive;
  const digitalUnlockedText =
    event.discountType === "price_break" && event.discountPercentage
      ? `Unlocked ${event.discountPercentage}% discount!`
      : event.discountType === "photo_bundle"
      ? digitalSelected
        ? "Included in photo bundle"
        : "Photo bundle active"
      : "Unlocked discount!";

  const cartDigitalQty = (cart?.sales || []).filter(
    (s: any) => isDigitalDownload(s.productType) && !s.prepaid
  ).length;
  const digitalTiersSorted = [...(event.digitalDiscountTiers || [])].sort(
    (a: any, b: any) => a.photoCount - b.photoCount
  );
  const digitalActiveTier = cartDigitalQty > 0
    ? digitalTiersSorted.find(t => t.photoCount >= cartDigitalQty) ?? null
    : null;
  const digitalNextTier = digitalActiveTier
    ? digitalTiersSorted.find(t => t.photoCount > digitalActiveTier.photoCount) ?? null
    : null;
  const digitalTierFilled = digitalActiveTier !== null && cartDigitalQty === digitalActiveTier.photoCount;

  const DiscountStarIcon = ({ size = 12 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 10 10" fill="currentColor" aria-hidden="true" className="lightbox-print-volume-teaser-icon">
      <path d="M4.661 0.2115C4.724 0.082 4.856 0 5 0C5.1445 0 5.276 0.082 5.339 0.2115C5.826 1.2105 6.664 2.9315 6.664 2.9315C6.664 2.9315 8.5695 3.1935 9.675 3.346C9.8765 3.3735 10 3.544 10 3.7195C10 3.8145 9.964 3.911 9.8845 3.9875C9.0795 4.7565 7.6935 6.083 7.6935 6.083C7.6935 6.083 8.032 7.9665 8.228 9.059C8.2695 9.29 8.0905 9.5 7.857 9.5C7.796 9.5 7.735 9.4855 7.6795 9.4555C6.6955 8.9315 5 8.03 5 8.03C5 8.03 3.3045 8.9315 2.3205 9.4555C2.265 9.4855 2.2035 9.5 2.1425 9.5C1.91 9.5 1.73 9.2895 1.772 9.059C1.9685 7.9665 2.307 6.083 2.307 6.083C2.307 6.083 0.9205 4.7565 0.116 3.9875C0.0360001 3.911 0 3.8145 0 3.72C0 3.544 0.1245 3.373 0.3255 3.346C1.431 3.1935 3.336 2.9315 3.336 2.9315C3.336 2.9315 4.1745 1.2105 4.661 0.2115ZM5 1.228L3.8195 3.624L1.1965 3.9835L3.1205 5.805L2.6465 8.4325L5 7.18L7.3535 8.4325L6.878 5.8145L8.8035 3.9835L6.1465 3.6055L5 1.228Z" />
    </svg>
  );

  const PrepaidIcon = ({ size = 12 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" className="lightbox-print-volume-teaser-icon" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16"/>
    </svg>
  );

  const fetchPrintProductsData = async () => {
    try {
      setLoading(true);
      const products = await fetchPrintProductsForPhoto(photo.id);
      setPrintProducts(products);
    } catch (error) {
      console.error("Error fetching print products:", error);
    } finally {
      setLoading(false);
    }
  };

  // Syncs local component state with existing cart data on mount/cart changes
  const initializeFromCart = async () => {
    try {
      if (!cartId) {
        if (onCartStatusChange) {
          onCartStatusChange(false);
        }
        return;
      }

      const cartData = await fetchCart(cartId);
      if (cartData && cartData.sales) {
        const hasDigitalPhoto = cartData.sales.some(
          (sale: any) =>
            isDigitalDownload(sale.productType) && sale.photoId === photo.id
        );

        const photoSales = cartData.sales.filter(
          (sale: any) => sale.photoId === photo.id
        );
        setExistingSales(photoSales);

        const quantities: { [key: number]: number } = {};
        photoSales.forEach((sale: any) => {
          if (sale.productType === "print_product" && sale.printProductId) {
            quantities[sale.printProductId] =
              (quantities[sale.printProductId] || 0) + sale.quantity;
          }
        });

        setProductQuantities(quantities);
        setDigitalSelected(hasDigitalPhoto);

        // Notify parent component about initial cart status
        if (onCartStatusChange) {
          const hasCartItems =
            (!photo.digitalDownloadDisabled && hasDigitalPhoto) ||
            Object.values(quantities).some(qty => qty > 0);
          onCartStatusChange(hasCartItems);
        }
      } else {
        if (onCartStatusChange) {
          onCartStatusChange(false);
        }
      }
    } catch (error) {
      console.error("Error initializing from cart:", error);
      if (onCartStatusChange) {
        onCartStatusChange(false);
      }
    }
  };

  const toggleDescription = (productId: number) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const isDescriptionLong = (description: string) => {
    return description && description.length > 100;
  };

  useEffect(() => {
    const handleResize = () => {
      setIsWideDesktop(window.innerWidth >= 1281);
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (closeTimerRef.current !== null) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const openPreview = (productId: number) => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    const el = thumbnailRefs.current.get(productId);
    if (el) {
      const rect = el.getBoundingClientRect();
      setActiveThumbY(rect.top + rect.height / 2);
    }
    setActiveProductId(productId);
  };

  const scheduleClose = () => {
    closeTimerRef.current = window.setTimeout(() => {
      setActiveProductId(null);
      closeTimerRef.current = null;
    }, 80);
  };

  const cancelClose = () => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // Watch for cart updates and sync local state
  useEffect(() => {
    if (!cart?.sales || !photo?.id) return;

    const photoSales = cart.sales.filter(
      (sale: any) => sale.photoId === photo.id
    );

    setExistingSales(photoSales);

    // Update digital selection
    const hasDigitalPhoto = photoSales.some((sale: any) =>
      isDigitalDownload(sale.productType)
    );
    setDigitalSelected(hasDigitalPhoto);

    // Update product quantities
    const quantities: { [key: number]: number } = {};
    photoSales.forEach((sale: any) => {
      if (sale.productType === "print_product" && sale.printProductId) {
        quantities[sale.printProductId] =
          (quantities[sale.printProductId] || 0) + sale.quantity;
      }
    });
    setProductQuantities(quantities);

    // Update cart status for parent component
    if (onCartStatusChange) {
      const hasCartItems =
        (!photo.digitalDownloadDisabled && hasDigitalPhoto) ||
        Object.values(quantities).some(qty => qty > 0);
      onCartStatusChange(hasCartItems);
    }
  }, [cart?.sales, photo.id]);

  const isCurrencyMismatch = () => {
    if (
      cart &&
      event.currency !== cart.currency &&
      (cart.photos?.length || 0) > 0
    ) {
      //@ts-ignore
      toastr.error(
        `The cart can only contain photos in one currency, currently ${event.currency}. Clear and try adding again.`
      );
      return true;
    }
    return false;
  };

  const isDifferentEventPhoto = () => {
    if (
      cart &&
      cart.eventIds &&
      cart.eventIds.length >= 1 &&
      !cart.eventIds.includes(event.id) &&
      (cart.photos?.length || 0) > 0
    ) {
      //@ts-ignore
      toastr.error(
        `Your cart has photos from a different event. Please clear your cart before adding photos from this event.`
      );
      return true;
    }
    return false;
  };

  const handleDigitalToggle = async () => {
    const newDigitalSelected = !digitalSelected;

    if (newDigitalSelected && isCurrencyMismatch()) {
      return;
    }
    if (newDigitalSelected && isDifferentEventPhoto()) {
      return;
    }

    if (
      newDigitalSelected &&
      canShowEmailPopup &&
      canShowEmailPopup() &&
      showSaveCartModal
    ) {
      showSaveCartModal(photo, {
        type: "digital_photo_download",
        photo_id: photo.id,
        quantity: 1
      });
      return;
    }
    try {
      const prevSelected = digitalSelected;
      setDigitalSelected(newDigitalSelected);

      const ok = await updateCartSurgically("digital", newDigitalSelected);
      if (!ok) {
        setDigitalSelected(prevSelected);
      }
    } catch (error) {
      console.error("Error in handleDigitalToggle:", error);
    }
  };

  const updateProductQuantity = async (productId: number, change: number) => {
    const currentQty = productQuantities[productId] || 0;
    const newQty = Math.max(0, currentQty + change);
    if (change > 0) shippingUnlockedProductIdRef.current = productId;
    const isAddingToCart = currentQty === 0 && newQty > 0;

    if (isAddingToCart && isCurrencyMismatch()) {
      return;
    }
    if (isAddingToCart && isDifferentEventPhoto()) {
      return;
    }

    if (
      isAddingToCart &&
      canShowEmailPopup &&
      canShowEmailPopup() &&
      showSaveCartModal
    ) {
      const product = printProducts.find(p => p.id === productId);
      const cartItem = {
        type: "print_product",
        photo_id: photo.id,
        print_product_id: productId,
        event_print_product_id: product?.eventPrintProductId,
        quantity: newQty
      };
      showSaveCartModal(photo, cartItem);
      return;
    }

    try {
      const prevQuantities = productQuantities;

      const nextQuantities = { ...productQuantities };
      if (newQty === 0) delete nextQuantities[productId];
      else nextQuantities[productId] = newQty;
      setProductQuantities(nextQuantities);

      const ok = await updateCartSurgically("product", newQty, productId);
      if (!ok) {
        // Revert UI on failure
        setProductQuantities(prevQuantities);
        return;
      }
    } catch (error) {
      console.error("Error in updateProductQuantity:", error);
    }
  };

  // Precisely updates specific cart items without affecting other items
  const updateCartSurgically = async (
    itemKey: string,
    newValue: boolean | number,
    productId?: number
  ) => {
    try {
      const updates: CartUpdateItem[] = [];

      if (itemKey === "digital") {
        // Use latest cart data to check for existing sales
        const latestSales = existingSales || cart?.sales;
        // Find existing digital sale with multiple property name formats
        let existingDigitalSale = latestSales.find(sale => {
          const productType = sale.productType || sale.product_type;
          const photoId = sale.photoId || sale.photo_id;
          return isDigitalDownload(productType) && photoId === photo.id;
        });

        if (newValue && !existingDigitalSale) {
          // Add new digital photo
          updates.push({
            product_type: "digital_photo_download",
            photo_id: photo.id,
            quantity: 1
          });
        } else if (!newValue && existingDigitalSale) {
          // Remove existing digital photo
          updates.push({ id: existingDigitalSale.id, _destroy: true });
        } else if (newValue && existingDigitalSale) {
          // Digital photo already exists - no need to do anything, it's already in cart
          // This handles the case where user clicks "Add to Cart" when it's already added
        }
      } else if (productId !== undefined) {
        // Use latest cart data to check for existing sales, prioritizing local state
        const latestSales =
          existingSales.length > 0 ? existingSales : cart?.sales || [];
        const quantity = newValue as number;
        const product = printProducts.find(p => p.id === productId);
        const eventPrintProductId = product?.eventPrintProductId;

        // Find existing sale for this specific product
        let existingSale = latestSales.find(
          (sale: any) =>
            (sale.productType === "print_product" ||
              sale.product_type === "print_product") &&
            (sale.photoId === photo.id || sale.photo_id === photo.id) &&
            (sale.printProductId === productId ||
              sale.print_product_id === productId ||
              sale.eventPrintProductId === eventPrintProductId ||
              sale.event_print_product_id === eventPrintProductId)
        );

        // Fallback: if only one print sale exists and it matches this product, use it
        if (!existingSale) {
          const printSales = latestSales.filter(
            (sale: any) =>
              (sale.productType === "print_product" ||
                sale.product_type === "print_product") &&
              (sale.photoId === photo.id || sale.photo_id === photo.id)
          );

          if (printSales.length === 1) {
            const sale = printSales[0];
            const isMatchingProduct =
              sale.printProductId === productId ||
              sale.print_product_id === productId ||
              sale.eventPrintProductId === eventPrintProductId ||
              sale.event_print_product_id === eventPrintProductId;

            if (isMatchingProduct) {
              existingSale = sale;
            }
          }
        }

        if (existingSale && quantity === 0) {
          updates.push({ id: existingSale.id, _destroy: true });
        } else if (existingSale && quantity > 0) {
          updates.push({ id: existingSale.id, quantity: quantity });
        } else if (!existingSale && quantity > 0 && product) {
          if (eventPrintProductId == null) {
            console.warn(
              `Missing eventPrintProductId for product ${productId}`
            );
          }

          updates.push({
            product_type: "print_product",
            photo_id: photo.id,
            print_product_id: productId,
            event_print_product_id: eventPrintProductId,
            quantity: quantity
          });
        }
      }
      if (updates.length === 0) {
        return true;
      }
      const success = await updateCart(updates);
      return success;
    } catch (error) {
      console.error("Error updating cart surgically:", error);
      return false;
    }
  };

  // Sends cart update requests to the API and syncs response with local state
  const updateCart = async (
    items: CartUpdateItem[],
    skipSync: boolean = false
  ) => {
    try {
      // Allow cartId to be null - backend will handle cart creation
      const response = await updateCartItems(cartId, items);

      if (updateCartFromApiResponse && response) {
        updateCartFromApiResponse(response);

        // Force immediate sync of local state with the updated cart data
        if (response.cart && response.cart.sales) {
          const photoSales = response.cart.sales.filter(
            (sale: any) =>
              sale.photoId === photo.id || sale.photo_id === photo.id
          );
          setExistingSales(photoSales);

          // Also update cart status for parent component (cart badge)
          if (onCartStatusChange) {
            const hasDigitalPhoto = photoSales.some((sale: any) =>
              isDigitalDownload(sale.productType)
            );
            const hasPrintProducts = photoSales.some(
              (sale: any) =>
                sale.productType === "print_product" && sale.quantity > 0
            );
            const hasCartItems =
              (!photo.digitalDownloadDisabled && hasDigitalPhoto) ||
              hasPrintProducts;
            onCartStatusChange(hasCartItems);
          }
        }
      }

      return response !== null;
    } catch (error) {
      console.error("Error updating cart:", error);
      return false;
    }
  };

  const activeProduct =
    activeProductId !== null
      ? (printProducts.find(p => p.id === activeProductId) ?? null)
      : null;

  return (
    <div className="lightbox-print-products" ref={printProductsPanelRef}>
      {loading ? (
        <div className="loading-section">
          <i className="fa fa-spinner fa-spin"></i>
          <span>Loading products...</span>
        </div>
      ) : (
        <>
          <div className="hidden-xs">
            <PrintLightboxHeader
              cartId={cartId ?? undefined}
              primaryColor={primaryColor || "#259fa5"}
              hasCartItems={existingSales.some(
                sale =>
                  sale.productType === "print_product" ||
                  (isDigitalDownload(sale.productType) &&
                    !photo.digitalDownloadDisabled)
              )}
              isEmbed={isEmbed}
            />
          </div>
          <div className="print-products-content">
            {!photo.digitalDownloadDisabled && (
              <div className="lightbox-print-product-card digital-product">
                <div className="lightbox-print-product-thumbnail-column">
                  <div className="lightbox-print-product-thumbnail">
                    <img
                      src={photo.thumbnailUrl || photo.watermarkedImg}
                      alt="Digital version"
                      className="lightbox-print-product-image"
                    />
                  </div>
                </div>

                <div className="lightbox-print-product-info-column">
                  <h4 className="lightbox-print-product-title">
                    Digital version
                  </h4>
                  <p className="lightbox-print-desc lightbox-print-product-description">
                    High-resolution digital download
                  </p>
                  {!digitalDiscountShown && (prepaidFree || digitalDiscountTeaser) && !isBundleDiscountActive && (
                    <div
                      className="lightbox-print-discounts-available"
                      style={{ color: prepaidFree ? digitalDiscountColor : "#00A8FF", whiteSpace: "nowrap" }}
                    >
                      <DiscountStarIcon />
                      Discounts available
                    </div>
                  )}
                  {!digitalDiscountShown &&
                    event.discountType === "photo_bundle" &&
                    digitalDiscountTeaser &&
                    setSelfieUploadPopup && (
                      <button
                        type="button"
                        className="lightbox-print-discounts-available lightbox-bundle-offer-link"
                        style={{ color: "#00A8FF", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", alignItems: "flex-start", whiteSpace: "normal" }}
                        onClick={() => setSelfieUploadPopup(true)}
                      >
                        <DiscountStarIcon />
                        {digitalDiscountTeaser}
                      </button>
                    )}
                </div>
                <div className="lightbox-print-product-controls-column">
                  <div className="lightbox-price-discount-block">
                    <div className="lightbox-print-product-price">
                      {prepaidFree ? (
                        <>
                          <span style={{ textDecoration: "line-through", fontSize: "11px", color: "#888", marginRight: "4px", fontWeight: "normal" }}>
                            {event.displayPrice}
                          </span>
                          FREE
                        </>
                      ) : digitalDiscountApplied && event.discountType !== "photo_bundle" ? (
                        <>
                          <span className="lightbox-print-original-price">
                            {digitalSale.originalTotalFormatted.split(" ")[0]}
                          </span>
                          <span className="lightbox-print-discounted-price">
                            {digitalSale.digitalDiscountedTotalFormatted}
                          </span>
                        </>
                      ) : `${event.displayPrice} ${event.currency}`}
                    </div>
                    {prepaidFree && (
                      <div className="lightbox-print-volume-teaser" style={{ color: "#8013AE", fontSize: "10px" }}>
                        <PrepaidIcon size={10} />
                        Pre-paid
                      </div>
                    )}
                    {event.discountType === "multi_tier" ? (
                      digitalTierFilled && digitalActiveTier ? (
                        <div className="lightbox-discount-info">
                          <div className="lightbox-discount-unlocked" style={{ color: "#00A8FF" }}>
                            <DiscountStarIcon size={10} />
                            {`Unlocked: ${digitalActiveTier.photoCount} photos for ${digitalActiveTier.formattedPrice}!`}
                          </div>
                          {digitalNextTier && digitalNextTier.photoCount - cartDigitalQty > 0 && (
                            <div className="lightbox-discount-next-tier lightbox-digital-discount">
                              Add {digitalNextTier.photoCount - cartDigitalQty} more (any photo) to get{" "}
                              {digitalNextTier.photoCount} photos for {digitalNextTier.formattedPrice}
                            </div>
                          )}
                        </div>
                      ) : !prepaidFree && digitalDiscountTeaser ? (
                        <>
                          <div className="lightbox-print-volume-teaser" style={{ color: "#00A8FF", fontSize: "10px", whiteSpace: "nowrap" }}>
                            <DiscountStarIcon size={10} />
                            {digitalDiscountTeaser}
                          </div>
                          {digitalActiveTier && cartDigitalQty > 0 && (
                            <div className="lightbox-discount-next-tier lightbox-digital-discount">
                              Add {digitalActiveTier.photoCount - cartDigitalQty} more (any photo) to get{" "}
                              {digitalActiveTier.photoCount} photos for {digitalActiveTier.formattedPrice}
                            </div>
                          )}
                        </>
                      ) : null
                    ) : digitalDiscountShown ? (
                      <div className="lightbox-discount-unlocked" style={{ color: "#00A8FF" }}>
                        <DiscountStarIcon size={10} />
                        {digitalUnlockedText}
                      </div>
                    ) : !prepaidFree &&
                      digitalDiscountTeaser &&
                      event.discountType !== "photo_bundle" ? (
                      <div className="lightbox-print-volume-teaser" style={{ color: "#00A8FF", fontSize: "10px", whiteSpace: "nowrap" }}>
                        <DiscountStarIcon size={10} />
                        {digitalDiscountTeaser}
                      </div>
                    ) : null}
                  </div>
                  <button
                    className={`${
                      !digitalSelected && !hasAnimatedRef.current
                        ? "geo-high-animation"
                        : "geo-btn-bordered"
                    } lightbox-print-product-action-btn geo-btn geo-btn--small view-cart-btn ${
                      digitalSelected ? "selected" : ""
                    }`}
                    onClick={() => {
                      hasAnimatedRef.current = true;
                      handleDigitalToggle();
                    }}
                    style={{
                      border: `1px solid ${primaryColor}`,
                      width: !digitalSelected && digitalBundleActive ? "115px" : "90px"
                    }}
                  >
                    <span
                      style={{
                        backgroundColor: digitalSelected
                          ? "transparent"
                          : "#fff",

                        color: primaryColor,
                        padding: "1px 12px"
                      }}
                    >
                      {digitalSelected ? "Remove" : digitalBundleActive ? "Add to bundle" : "Add to Cart"}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {!photo.videoContent &&
              printProducts.map(product => {
                const openMobileZoom = () => {
                  setActiveProductId(product.id);
                  Telemetry.track("event-gallery:print-product-zoom-opened", {
                    productId: product.id,
                    productTitle: product.title,
                    photoId: photo.id,
                    eventId: event.id,
                    trigger: "thumbnail-tap",
                    viewport: "mobile"
                  });
                };
                const otherPhotosQty = (cart?.sales || [])
                  .filter((s: any) => s.productType === "print_product" && s.printProductId === product.id && s.photoId !== photo.id)
                  .reduce((sum: number, s: any) => sum + (s.quantity || 1), 0);
                const globalQty = (productQuantities[product.id] || 0) + otherPhotosQty;
                const currentTier = product.discountTiers.length > 0
                  ? applicableTier(product.discountTiers, globalQty)
                  : null;
                const hasUnlockedDiscount = globalQty > 0 && currentTier !== null;
                const nextTier = hasUnlockedDiscount && currentTier
                  ? ([...product.discountTiers].sort((a, b) => a.minQuantity - b.minQuantity)
                      .find(t => t.minQuantity > currentTier.minQuantity) ?? null)
                  : null;
                const moreNeeded = nextTier ? nextTier.minQuantity - globalQty : null;
                const discountedPriceFormatted = hasUnlockedDiscount && currentTier
                  ? (() => {
                      const discounted = discountedUnitPrice(product.totalPrice, product.discountTiers, globalQty);
                      const prefix = product.totalPriceFormatted.match(/^[^\d]*/)?.[0] || '';
                      return `${prefix}${discounted.toFixed(2)} ${product.currency}`;
                    })()
                  : null;
                return (
                <div
                  key={product.id}
                  data-product-id={product.id}
                  className="lightbox-print-product-card print-product"
                >
                  <div
                    className="lightbox-print-product-thumbnail-column"
                    role="button"
                    tabIndex={0}
                    aria-label={`Preview ${product.title}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (isMobile) { openMobileZoom(); } else { setPinnedProductId(product.id); openPreview(product.id); }
                      }
                    }}
                    onMouseEnter={() => !isMobile && !pinnedProductId && openPreview(product.id)}
                    onMouseLeave={() => !isMobile && !pinnedProductId && scheduleClose()}
                    onClick={!isMobile ? () => { setPinnedProductId(product.id); openPreview(product.id); } : openMobileZoom}
                    style={isMobile ? { cursor: "pointer" } : undefined}
                  >
                    <div className="lightbox-print-product-thumbnail">
                      {product.previewImageThumbnailURL ? (
                        <img
                          ref={el => {
                            if (el) thumbnailRefs.current.set(product.id, el);
                            else thumbnailRefs.current.delete(product.id);
                          }}
                          src={product.previewImageThumbnailURL}
                          alt={product.title}
                          className="lightbox-print-product-image"
                        />
                      ) : (
                        <div
                          ref={el => {
                            if (el) thumbnailRefs.current.set(product.id, el);
                            else thumbnailRefs.current.delete(product.id);
                          }}
                          className="placeholder-image"
                        >📷</div>
                      )}
                      <span aria-hidden="true" className="print-product-magnify-btn">
                        <MagnifyIcon size={11} />
                      </span>
                    </div>
                  </div>

                  <div className="lightbox-print-product-info-column">
                    <h4 className="lightbox-print-product-title">
                      {product.title}
                    </h4>
                    {(product.discountTiers.length > 0 || product.freeShipping) && (
                      <div className="lightbox-print-discounts-available" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px" }}>
                        {product.discountTiers.length > 0 && (
                          <span style={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "center" }}>
                            <img src={starIcon} alt="" aria-hidden="true" className="lightbox-print-volume-teaser-icon" />
                            <span style={{ color: "#e6920a" }}>Discounts available</span>
                          </span>
                        )}
                        {product.freeShipping && (
                          <span style={{ color: "#e6920a", whiteSpace: "nowrap" }}>Free shipping</span>
                        )}
                      </div>
                    )}
                    <button
                      className="lightbox-print-see-more-btn"
                      onClick={() => { if (isMobile) { openMobileZoom(); } else { setPinnedProductId(product.id); openPreview(product.id); } }}
                      style={{ color: "#259fa5" }}
                    >
                      See more
                    </button>
                  </div>

                  <div className="lightbox-print-product-controls-column">
                    <div className="lightbox-price-discount-block">
                      <div className="lightbox-print-product-price">
                        {hasUnlockedDiscount && discountedPriceFormatted ? (
                          <>
                            <span className="lightbox-print-original-price">
                              {product.totalPriceFormatted.split(" ")[0]}
                            </span>
                            <span className="lightbox-print-discounted-price">
                              {discountedPriceFormatted}
                            </span>
                          </>
                        ) : product.totalPriceFormatted}
                      </div>
                      {product.discountTiers.length > 0 && (
                        hasUnlockedDiscount && currentTier ? (
                          <div className="lightbox-discount-info">
                            <div className="lightbox-discount-unlocked">
                              <img src={starIcon} alt="" aria-hidden="true" className="lightbox-print-volume-teaser-icon" />
                              {currentTier.kind === "percentage"
                                ? `Unlocked ${currentTier.value}% discount!`
                                : "Unlocked discount!"}
                            </div>
                            {nextTier && moreNeeded !== null && moreNeeded > 0 && (
                              <div className="lightbox-discount-next-tier">
                                Add {moreNeeded} more (any photo){" "}
                                {nextTier.kind === "percentage"
                                  ? `to get ${nextTier.value}% off`
                                  : "for a bundle price"}
                              </div>
                            )}
                          </div>
                        ) : (() => {
                          const teaser = firstTierOfferText(product.discountTiers);
                          return teaser ? (
                            <div className="lightbox-print-volume-teaser">
                              <img src={starIcon} alt="" aria-hidden="true" className="lightbox-print-volume-teaser-icon" />
                              {teaser}
                            </div>
                          ) : null;
                        })()
                      )}
                    </div>
                    {productQuantities[product.id] > 0 ? (
                      <div className="quantity-controls">
                        <div className="quantity-labels-container">
                          <span className="quantity-label">Quantity</span>
                        </div>
                        <div className="quantity-selector-container">
                          <div className="lightbox-print-quantity-selector">
                            <button
                              className="lightbox-print-quantity-btn minus"
                              onClick={() =>
                                updateProductQuantity(product.id, -1)
                              }
                              style={{
                                border: `1px solid ${primaryColor}`
                              }}
                            >
                              −
                            </button>
                            <span className="lightbox-print-quantity-display">
                              {productQuantities[product.id] || 0}
                            </span>
                            <button
                              className="lightbox-print-quantity-btn plus"
                              onClick={() =>
                                updateProductQuantity(product.id, 1)
                              }
                              style={{
                                border: `1px solid ${primaryColor}`
                              }}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        className={`${
                          !hasAnimatedRef.current
                            ? "geo-high-animation"
                            : "geo-btn-bordered"
                        } lightbox-print-product-action-btn geo-btn geo-btn--small view-cart-btn`}
                        onClick={() => {
                          hasAnimatedRef.current = true;
                          updateProductQuantity(product.id, 1);
                        }}
                        style={{
                          border: `1px solid ${primaryColor}`
                        }}
                      >
                        <span
                          style={{
                            backgroundColor: "#fff",
                            color: primaryColor
                          }}
                        >
                          Add to Cart
                        </span>
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
          </div>
        </>
      )}
      {bundleAppliedVisible && (
        <div className="lightbox-photo-bundle-applied">
          <div className="lightbox-shipping-inner">
            <DiscountStarIcon size={14} />
            <span>Photo bundle applied!</span>
          </div>
        </div>
      )}
      {shippingUnlockedVisible && (
        <div className="lightbox-free-shipping-unlocked" style={{ bottom: unlockedBannerBottom }}>
          <div className="lightbox-shipping-inner">
            <svg width="14" height="12" viewBox="0 0 24 18" fill="none" aria-hidden="true">
              <path d="M1 1h14v11H1V1zm14 3h4l3 4v4h-7V4zM5.5 16a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm11 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Free shipping unlocked!</span>
          </div>
        </div>
      )}
      {shippingReminderVisible && cart?.freeShippingAmountNeeded && (
        <div className="lightbox-shipping-reminder">
          <img src={truckIcon} alt="" width={10} height={9} aria-hidden="true" />
          You're {cart.freeShippingAmountNeeded} away from free print shipping!
        </div>
      )}
      {!isWideDesktop && !isMobile && activeProduct && (
        <PrintProductPreview
          product={activeProduct}
          variant="compact"
          thumbCenterY={activeThumbY}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          pinned={pinnedProductId === activeProduct.id}
          onClose={() => { setPinnedProductId(null); setActiveProductId(null); }}
        />
      )}
      {isWideDesktop && activeProduct && (
        <PrintProductPreview
          product={activeProduct}
          variant="wide"
          thumbCenterY={activeThumbY}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          pinned={pinnedProductId === activeProduct.id}
          onClose={() => { setPinnedProductId(null); setActiveProductId(null); }}
        />
      )}
      {isMobile && activeProduct && (
        <PrintProductZoomDrawer
          product={activeProduct}
          isDescriptionExpanded={!!expandedDescriptions[activeProduct.id]}
          showSeeMore={!!isDescriptionLong(activeProduct.description)}
          onToggleDescription={() => toggleDescription(activeProduct.id)}
          onClose={() => {
            Telemetry.track("event-gallery:print-product-zoom-closed", {
              productId: activeProduct.id,
              productTitle: activeProduct.title,
              photoId: photo.id,
              eventId: event.id
            });
            setActiveProductId(null);
          }}
        />
      )}
    </div>
  );
};

export default LightBoxPrintProducts;
